import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type {
  SupplyId,
  SupplyKind,
  SupplyUseInput,
} from "@frontier/content/supplies";
import {
  supplyControlsSchema,
  supplyUseAckSchema,
  type SupplyUseAck,
  type SupplyControls,
  type SupplyStationView,
} from "@frontier/protocol/supplies";
import type { Profile } from "@frontier/protocol/contracts";
import { browserAuthStorage } from "./auth-storage";
import { ApiError, api, requestId } from "./session-api";
import type { ProfileSync } from "./profile-sync";
import {
  archiveSupplyIntent,
  readSupplyIntent,
  readSupplyUse,
  sameSupplyContext,
  type SupplyIntent,
} from "./supply-intents";

interface SupplyRoom {
  send(type: string, message: object): void;
}
const stockKey = (id: string) => `frontier.supplies.${id}`;
const useKey = (id: string) => `frontier.supplyUse.${id}`;

export function useSupplyActions(
  profile: Profile | null,
  token: RefObject<string>,
  room: RefObject<SupplyRoom | null>,
  sync: ProfileSync,
  connected: boolean,
  setBusy: (value: boolean) => void,
  setNotice: (value: string) => void,
) {
  const [storage] = useState(browserAuthStorage);
  const [station, setStation] = useState<SupplyStationView | null>(null);
  const [controls, setControls] = useState<SupplyControls | null>(null);
  const [pending, setPending] = useState<SupplyIntent | null>(null);
  const [recovery, setRecovery] = useState(false);
  const active = useRef(profile);
  active.current = profile;
  const controlRef = useRef<SupplyControls | null>(null);
  const readSequence = useRef(0);
  const executing = useRef(false);
  const currentPlayer = useCallback(() => {
    const latest = sync.current();
    return latest?.playerId === active.current?.playerId
      ? latest
      : active.current;
  }, [sync]);
  const ownsSession = useCallback(
    (player: Profile, ticket: number) =>
      ticket === sync.ticket() && currentPlayer()?.playerId === player.playerId,
    [currentPlayer, sync],
  );
  const reload = useCallback(async () => {
    const player = currentPlayer();
    const ticket = sync.ticket();
    const sequence = ++readSequence.current;
    if (!player || player.hullRevision === undefined || !token.current)
      return null;
    try {
      const value = await api.supplies(token.current);
      const current = currentPlayer();
      if (
        ownsSession(player, ticket) &&
        current &&
        sequence === readSequence.current &&
        sameSupplyContext(player, current) &&
        sameSupplyContext(value, current)
      ) {
        setStation(value);
        return value;
      }
    } catch (error) {
      if (ownsSession(player, ticket) && sequence === readSequence.current) {
        console.warn("supply-stock-read-failed", {
          playerId: player.playerId,
          hullId: player.hullId,
          hullRevision: player.hullRevision,
          message: error instanceof Error ? error.message : "Unknown failure",
        });
        setNotice("Не удалось обновить припасы. Повтори попытку.");
      }
    }
    return null;
  }, [token, sync, setNotice, currentPlayer, ownsSession]);
  const checkRecovery = useCallback(() => {
    const player = currentPlayer();
    if (!player) {
      setRecovery(false);
      return;
    }
    const raw = storage.getItem(stockKey(player.playerId));
    const useRaw = storage.getItem(useKey(player.playerId));
    const use = readSupplyUse(useRaw);
    const currentControls = controlRef.current;
    setRecovery(
      (!!raw && !readSupplyIntent(raw)) ||
        (!!useRaw &&
          (!use ||
            !sameSupplyContext(use, player) ||
            (!!currentControls && use.lifeSeq !== currentControls.lifeSeq))),
    );
  }, [storage, currentPlayer]);
  useEffect(() => {
    executing.current = false;
    setPending(
      profile
        ? readSupplyIntent(storage.getItem(stockKey(profile.playerId)))
        : null,
    );
  }, [profile?.playerId, storage]);
  useEffect(() => {
    readSequence.current++;
    setStation(null);
    setControls(null);
    controlRef.current = null;
    checkRecovery();
    if (profile?.docked) void reload();
  }, [
    profile?.playerId,
    profile?.runId,
    profile?.hullId,
    profile?.hullRevision,
    profile?.docked,
    reload,
    checkRecovery,
  ]);

  const execute = useCallback(
    async (intent: SupplyIntent) => {
      const player = currentPlayer();
      if (!player || executing.current) return;
      const ticket = sync.ticket();
      const key = stockKey(player.playerId);
      const raw = JSON.stringify(intent);
      const saved = storage.getItem(key);
      if (
        saved &&
        readSupplyIntent(saved)?.input.requestId !== intent.input.requestId
      )
        return;
      storage.setItem(key, raw);
      setPending(intent);
      setBusy(true);
      executing.current = true;
      try {
        const result =
          intent.operation === "purchase"
            ? await api.purchaseSupply(token.current, intent.input)
            : await api.transferSupply(token.current, intent.input);
        if (!ownsSession(player, ticket)) return;
        if (storage.getItem(key) === raw) {
          storage.removeItem(key);
          setPending(null);
        }
        const current = currentPlayer();
        let confirmed: SupplyStationView | undefined;
        if (
          current &&
          sameSupplyContext(player, current) &&
          sameSupplyContext(result.profile, current) &&
          sameSupplyContext(result.supplies, current) && sync.commit(result.profile, ticket)
        ) {
          // Invalidate any read that started before this write completed.
          readSequence.current++;
          setStation(result.supplies);
          confirmed = result.supplies;
          setNotice(
            intent.operation === "purchase"
              ? "Припасы добавлены на склад."
              : "Запас в трюме обновлён.",
          );
        } else await sync.refresh(ticket);
        room.current?.send("syncRisk", {});
        void reload();
        return confirmed;
      } catch (error) {
        if (!ownsSession(player, ticket)) return;
        const rejected =
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500 &&
          ![401, 408, 426, 429].includes(error.status) &&
          error.code !== "CLIENT_UPDATE_REQUIRED";
        if (rejected && storage.getItem(key) === raw) {
          storage.removeItem(key);
          setPending(null);
        }
        const current = currentPlayer();
        if (current && sameSupplyContext(player, current))
          setNotice(
            error instanceof Error
              ? error.message
              : "Результат операции неизвестен. Нажми «Проверить операцию».",
          );
        void reload();
      } finally {
        if (ownsSession(player, ticket)) {
          executing.current = false;
          setBusy(false);
          checkRecovery();
        }
      }
    },
    [
      sync,
      storage,
      token,
      room,
      setBusy,
      setNotice,
      reload,
      currentPlayer,
      ownsSession,
      checkRecovery,
    ],
  );

  const receiveControls = useCallback(
    (value: SupplyControls) => {
      const parsed = supplyControlsSchema.parse(value);
      const player = currentPlayer();
      if (
        !player ||
        !sameSupplyContext(parsed, player) ||
        (controlRef.current && parsed.lifeSeq < controlRef.current.lifeSeq)
      )
        return;
      controlRef.current = parsed;
      setControls(parsed);
      checkRecovery();
    },
    [currentPlayer, checkRecovery],
  );
  const receiveAck = useCallback(
    (value: SupplyUseAck) => {
      const ack = supplyUseAckSchema.parse(value);
      const player = currentPlayer();
      if (!player) return;
      const key = useKey(player.playerId);
      const saved = readSupplyUse(storage.getItem(key));
      if (
        !saved ||
        saved.requestId !== ack.requestId ||
        saved.hullId !== ack.hullId ||
        saved.hullRevision !== ack.hullRevision
      )
        return;
      storage.removeItem(key);
      checkRecovery();
      if (
        !ack.accepted &&
        sameSupplyContext(saved, player) &&
        saved.lifeSeq === controlRef.current?.lifeSeq
      )
        setNotice(ack.message);
    },
    [storage, setNotice, currentPlayer, checkRecovery],
  );
  const useBattery = useCallback(
    (kind: SupplyKind = "battery", targetId?: string) => {
      const player = currentPlayer();
      const current = controlRef.current;
      const selected = current?.items.find((item) => item.kind === kind);
      if (
        !connected ||
        !player ||
        !current ||
        !sameSupplyContext(current, player) ||
        current.pending ||
        !selected?.itemId ||
        selected.cooldownSeconds > 0
      )
        return;
      const key = useKey(player.playerId);
      const raw = storage.getItem(key);
      const saved = readSupplyUse(raw);
      if (
        raw &&
        (!saved ||
          !sameSupplyContext(saved, current) ||
          saved.lifeSeq !== current.lifeSeq)
      ) {
        setRecovery(true);
        setNotice("Проверь неподтверждённый расход в трюме на станции.");
        return;
      }
      const input: SupplyUseInput = saved ?? {
        requestId: requestId(),
        runId: current.runId,
        hullId: current.hullId,
        hullRevision: current.hullRevision,
        lifeSeq: current.lifeSeq,
        itemId: selected.itemId,
        ...(kind === "rocket" && targetId ? { targetId } : {}),
      };
      storage.setItem(key, JSON.stringify(input));
      room.current?.send("supplyUse", input);
    },
    [connected, storage, room, currentPlayer, setNotice],
  );
  useEffect(() => {
    if (!connected || !profile) return;
    const retry = () => {
      const player = currentPlayer();
      const current = controlRef.current;
      if (!player || !current || !sameSupplyContext(current, player)) return;
      const saved = readSupplyUse(storage.getItem(useKey(player.playerId)));
      if (
        saved &&
        sameSupplyContext(saved, current) &&
        saved.lifeSeq === current.lifeSeq
      )
        room.current?.send("supplyUse", saved);
    };
    retry();
    const timer = window.setInterval(retry, 3000);
    return () => window.clearInterval(timer);
  }, [
    connected,
    profile?.playerId,
    profile?.runId,
    profile?.hullId,
    profile?.hullRevision,
    storage,
    room,
    currentPlayer,
  ]);
  const recover = useCallback(async () => {
    const player = currentPlayer();
    if (!player?.docked || executing.current) return;
    const saved = [stockKey(player.playerId), useKey(player.playerId)].map(key => ({key, raw: storage.getItem(key)}));
    const value = await reload();
    const current = currentPlayer();
    if (!value || !current || !sameSupplyContext(player, current)) return;
    for (const {key, raw} of saved) {
      if (raw && storage.getItem(key) === raw && (key === useKey(player.playerId) || !readSupplyIntent(raw)))
        archiveSupplyIntent(storage, key, raw, requestId());
    }
    checkRecovery();
    setNotice("Запасы обновлены. Можно продолжать.");
  }, [currentPlayer, reload, storage, checkRecovery, setNotice]);
  const visibleStation =
    profile && station && sameSupplyContext(profile, station) ? station : null;
  return {
    supplyStation: visibleStation,
    supplyControls:
      profile && controls && sameSupplyContext(profile, controls)
        ? controls
        : null,
    pendingSupply: pending,
    supplyRecovery: recovery,
    recoverSupplies: recover,
    reloadSupplies: reload,
    receiveSupplyControls: receiveControls,
    receiveSupplyAck: receiveAck,
    useBattery,
    buySupplies: async (supplyId: SupplyId, quantity: number) => {
      if (
        !storage.getItem(stockKey(profile?.playerId ?? "")) &&
        !recovery &&
        visibleStation
      )
        return execute({
          operation: "purchase",
          input: {
            requestId: requestId(),
            supplyId,
            quantity,
            priceVersion: visibleStation.priceVersion,
          },
        });
    },
    transferSupplies: (
      supplyId: SupplyId,
      direction: "load" | "unload",
      quantity: number,
    ) => {
      if (
        !storage.getItem(stockKey(profile?.playerId ?? "")) &&
        !recovery &&
        visibleStation
      )
        void execute({
          operation: "transfer",
          input: {
            requestId: requestId(),
            runId: visibleStation.runId,
            hullId: visibleStation.hullId,
            hullRevision: visibleStation.hullRevision,
            supplyId,
            direction,
            quantity,
          },
        });
    },
    retrySupply: () => {
      if (pending) void execute(pending);
    },
  };
}
